import React, { useEffect } from "react";
import { Alert, Card, Spinner } from "react-bootstrap";
import { useSuperState } from "helpers/hooks";
import { useApp } from 'common/AppContext';
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ProductUsageGraph = props => {

    const [state, setState] = useSuperState({
        loading: false,
        error: null,
        graph_data: null
    });

    const { appstream_fleet_name } = props;
    const { api } = useApp();
    const { formatDate } = useIntl();

    let start_date = props?.start_date ? moment(props?.start_date).toISOString() : moment().subtract(30, 'days').toISOString();
    let end_date = props?.end_date ? moment(props?.end_date).toISOString() : moment().toISOString();

    const refreshGraph = async () => {

        try {

            const start = moment(start_date).startOf('day');
            const end = moment(end_date).endOf('day');
            const total_days = Math.abs(start.diff(end, 'days')) + 1;

            setState({ loading: true });

            let { results } = await api.get(`/api/appstream/daily/product-usage`, { appstream_fleet_name, start_date, end_date });

            let graph_data = [];

            for (let day = 0; day < total_days; day++) {
                let dayData = {
                    name: formatDate(moment(start_date).add(day, 'days').toDate(), {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })
                };

                Object.keys(results).forEach(productName => {
                    dayData[productName] = results[productName][day];
                });

                graph_data.push(dayData);
            }

            setState({
                graph_data,
                loading: false
            });

        } catch ({ err }) {

            setState({
                loading: false,
                error: err
            });

        }

    };

    const CustomYAxisLabel = () => {
        const x_co = (475 / 2) * -1;

        return (
            <text
                style={{ fontWeight: 500, fill: '#141414' }}
                x={x_co}
                y={20}
                transform="rotate(-90)" dy={-4} fontSize={14} textAnchor="middle">Sessions</text>
        );

    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {

            // Keep only items with value > 0
            const filteredPayload = payload.filter(item => item.value > 0);

            if (filteredPayload.length === 0) {
                return null; // no data to show
            }

            return (
                <div className="solvably-graph-tooltip ms-4">
                    <p className="name m-0">Sessions started</p>
                    <p className="date m-0 mb-2">{label}</p>
                    {filteredPayload.map(item => (
                        <p key={item.dataKey} className="mb-0">
                            {item.dataKey}: {item.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomActiveDot = props => {
        const { cx, cy, value } = props;

        if (value <= 0) return null;

        return (
            <svg>
                <circle cx={cx} cy={cy} strokeWidth="2" opacity={0.2} fill="black" r="12.5" ></circle>
                <circle cx={cx} cy={cy} strokeWidth="2" stroke="black" fill="white" r="6" ></circle>
            </svg>
        );
    };

    useEffect(() => {
        refreshGraph();
    }, [appstream_fleet_name, start_date, end_date]);

    const { loading, error, graph_data } = state;

    return (
        <>
            <Card {...props}>
                <Card.Header>Product Usage</Card.Header>
                <Card.Body>
                    <Alert variant="danger" show={error} onClose={() => setState({ error: null })} dismissible>
                        {error}
                    </Alert>
                    {loading &&
                        <div className="text-center">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                    }
                    {!loading && <>
                        {graph_data ?
                                (
                                    <div className="w-100" style={{ height: '475px' }}>
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            <LineChart
                                                data={graph_data}
                                                margin={{
                                                    top: 5, right: 30, left: 20, bottom: 5
                                                }}
                                            >
                                                <CartesianGrid vertical={false} stroke="#d1d1d1"/>
                                                <XAxis dataKey="name"/>
                                                <YAxis label={<CustomYAxisLabel/>}/>
                                                <Tooltip content={CustomTooltip}/>
                                                <Legend/>
                                                {Object.keys(graph_data[0]).map((key, i) => {
                                                    if (key !== "name") {
                                                        return (
                                                            <Line
                                                                key={key}
                                                                type="monotone"
                                                                dataKey={key}
                                                                strokeWidth={3}
                                                                stroke={`hsl(${(i * 360) / graph_data.length}, 70%, 50%)`}
                                                                dot={false}
                                                                activeDot={<CustomActiveDot/>}
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })}

                                            </LineChart>
                                        </ResponsiveContainer>

                                    </div>
                                )
                                :
                                (
                                    <div className="d-flex align-items-center justify-content-center text-muted h-100">
                                        <FormattedMessage
                                            id="label.no_usage_data_found"
                                            defaultMessage="No usage data found"
                                        />
                                    </div>
                                )
                        }
                        </>
                    }
                </Card.Body>
            </Card>
        </>
    );

};

export default ProductUsageGraph;
